import { useEffect } from "react"
import { motion } from "motion/react"

import FeatureList from "../../components/FeatureList/FeatureList"
import FixedCloseButton from "../../components/FixedCloseButton/FixedCloseButton"
import PaywallListFooter from "../../components/PaywallListFooter/PaywallListFooter"
import Terms from "../../components/Terms/Terms"
import { useAppState } from "../../context/appStateContext"
import { PurchasProvider } from "../../context/purchaseContext"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import useStrings from "../../localization/useStrings"
import native from "../../native/native"
import styles from "./LossAversionPaywall.module.scss"

const SECONDS_PER_DAY = 60 * 60 * 24

// The colored boxes at the top. Images live in public/img/lossAversion/
// (photo1.png … photo8.png) and replace the fallback colors. Edit the file
// names / extensions below if yours differ.
const PHOTO_CELLS = [
  { color: "#973b6c", image: "photo1.png" },
  { color: "#8e0909", image: "photo2.png" },
  { color: "#57d25f", image: "photo3.png" },
  { color: "#fba80e", image: "photo4.png" },
  { color: "#ff9f9f", image: "photo5.png" },
  { color: "#8a3232", image: "photo6.png" },
  { color: "#2814e3", image: "photo7.png" },
  { color: "#86aaee", image: "photo8.png" },
]

const LossAversionPaywall = () => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <LossAversionPaywallContent />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

const LossAversionPaywallContent = () => {
  const { state, actions } = useAppState()
  const strings = useStrings().lossAversion

  useEffect(() => {
    // Let the photo grid bleed under the status bar; tint the home-indicator
    // area to match the footer sheet (same as the default paywall).
    actions.setIgnoreHeaderInset(true)
    actions.setFooterInsetColor("var(--background)")
  }, [actions])

  const topInset = state.initialData?.originalTopInset ?? 0

  const handleClose = () => {
    native.postNativeEvent("close")
  }

  return (
    <div className={styles.container}>
      <FixedCloseButton
        onClick={handleClose}
        topInset={topInset + 16}
        color="white"
      />
      <div className={styles.scrollArea}>
        <PhotoBanner />
        <StatCards />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={styles.headerText}
        >
          <h1 className={styles.title}>{strings.title}</h1>
          <p className={styles.subtitle}>{strings.subtitle}</p>
        </motion.div>

        <BackupCoverage />

        <div className={styles.premiumSection}>
          <h2 className={styles.premiumTitle}>{strings.premiumTitle}</h2>
          <FeatureList />
          <Terms />
        </div>
      </div>
      <PaywallListFooter />
    </div>
  )
}

const PhotoBanner = () => {
  return (
    <div className={styles.photoBanner}>
      <div className={styles.photoGrid}>
        {PHOTO_CELLS.map((cell, index) => (
          <div
            key={index}
            className={styles.photoCell}
            style={{
              backgroundColor: cell.color,
              backgroundImage: cell.image
                ? `url(${process.env.PUBLIC_URL}/img/lossAversion/${cell.image})`
                : undefined,
            }}
          />
        ))}
      </div>
      <div className={styles.bannerFade} />
    </div>
  )
}

const StatCards = () => {
  const productData = useAppState().state.productData
  const strings = useStrings().lossAversion

  const numPhotos = productData?.numPhotos ?? 0
  const daysAtRisk = productData
    ? Math.floor(productData.secondsSinceFirstUse / SECONDS_PER_DAY)
    : 0

  const cards = [
    { value: numPhotos.toLocaleString(), label: strings.photosUnprotected },
    { value: "0", label: strings.backedUp },
    { value: daysAtRisk.toLocaleString(), label: strings.daysAtRisk },
  ]

  return (
    <div className={styles.statCards}>
      {cards.map((card, index) => (
        <div key={index} className={styles.statCard}>
          <span className={styles.statValue}>{card.value}</span>
          <span className={styles.statLabel}>{card.label}</span>
        </div>
      ))}
    </div>
  )
}

const BackupCoverage = () => {
  const strings = useStrings().lossAversion

  return (
    <div className={styles.coverageBox}>
      <span className={styles.coverageTitle}>{strings.backupCoverage}</span>
      <div className={styles.coverageTrack}>
        <div className={styles.coverageFill} />
      </div>
      <span className={styles.coverageText}>{strings.vaultProtected(0)}</span>
    </div>
  )
}

export default LossAversionPaywall
